"use client";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Container,
  Fade,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  VerifiedUser as IntegrityIcon,
  Diamond as QualityIcon,
  Schedule as ReliabilityIcon,
  Lightbulb as InnovationIcon,
  FavoriteRounded as CustomerFocusIcon,
  Handshake as CollaborationIcon,
} from "@mui/icons-material";

const coreValues = [
  {
    title: "Integrity",
    description:
      "We uphold the highest standards of honesty, ethics and transparency in all our dealings.",
    icon: IntegrityIcon,
    color: "#FF6B6B",
  },
  {
    title: "Quality",
    description:
      "We are committed to delivering pharmaceutical products & services that meet or exceed the most stringent quality standards.",
    icon: QualityIcon,
    color: "#4ECDC4",
  },
  {
    title: "Reliability",
    description:
      "Our customers can rely on us for consistent, timely, and accurate delivery of their pharmaceutical needs.",
    icon: ReliabilityIcon,
    color: "#45B7D1",
  },
  {
    title: "Innovation",
    description:
      "We embrace innovation in our operations, processes, and technologies to continuously improve our services and offerings.",
    icon: InnovationIcon,
    color: "#FFA726",
  },
  {
    title: "Customer Focus",
    description:
      "We prioritize understanding and fulfilling the needs of our customers, providing personalized solutions and exceptional service.",
    icon: CustomerFocusIcon,
    color: "#AB47BC",
  },
  {
    title: "Collaboration",
    description:
      "We believe in the power of collaboration and actively seek partnerships with customers, suppliers, and stakeholders to achieve mutual success.",
    icon: CollaborationIcon,
    color: "#66BB6A",
  },
];

const CoreValues = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background:
          "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(10px)",
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h2"
            sx={{
              fontWeight: 700,
              color: "white",
              mb: 2,
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              background: "linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Core Values
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              maxWidth: "600px",
              mx: "auto",
              fontWeight: 300,
            }}
          >
            The principles that guide everything we do in pharmaceutical
            excellence
          </Typography>
        </Box>

        {/* Values Grid */}
        <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
          {coreValues.map((value, index) => {
            const IconComponent = value.icon;
            return (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Fade in timeout={600 + index * 200}>
                  <Card
                    sx={{
                      height: "100%",
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(20px)",
                      borderRadius: 4,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: `linear-gradient(90deg, ${value.color}, ${value.color}aa)`,
                        transform: "scaleX(0)",
                        transformOrigin: "left",
                        transition: "transform 0.4s ease",
                      },
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px ${value.color}33`,
                        "&::before": {
                          transform: "scaleX(1)",
                        },
                        "& .value-icon": {
                          transform: "scale(1.1) rotate(5deg)",
                          color: value.color,
                        },
                        "& .value-title": {
                          color: value.color,
                        },
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 3, md: 4 },
                        textAlign: "center",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          mb: 3,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: 80,
                          height: 80,
                          mx: "auto",
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${value.color}15, ${value.color}25)`,
                          border: `2px solid ${value.color}30`,
                        }}
                      >
                        <IconComponent
                          className="value-icon"
                          sx={{
                            fontSize: 40,
                            color: value.color,
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        />
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h5"
                        component="h3"
                        className="value-title"
                        sx={{
                          fontWeight: 700,
                          color: "#2c3e50",
                          mb: 2,
                          transition: "color 0.3s ease",
                        }}
                      >
                        {value.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#546e7a",
                          lineHeight: 1.6,
                          fontSize: "0.95rem",
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom Accent */}
        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            textAlign: "center",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              fontStyle: "italic",
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            "Excellence in pharmaceutical care through unwavering commitment to
            our values"
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CoreValues;
